import { useState } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import { Edit, Delete, Close } from "@mui/icons-material";
import { TimeAgo } from "@/components/TimeAgo";
import { useAppSelector } from "@/store";
import { useNotification } from "@/context/NotificationContext";
import { extractErrorMessage } from "@/api/axios";
import * as commentsApi from "@/api/comments.api";
import { hr } from "@/constants/strings.hr";
import type { Comment } from "@/types";

export function CommentItem({
  comment,
  onUpdated,
  onDeleted,
}: {
  comment: Comment;
  onUpdated: (updated: Comment) => void;
  onDeleted: (id: string) => void;
}) {
  const { user } = useAppSelector((s) => s.auth);
  const { notify } = useNotification();
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const isOwn = user?.id === comment.userId;
  const isAdmin = Boolean(user?.isAdmin);
  const canDelete = isOwn || isAdmin;
  const canEdit = isOwn;

  const handleUpdate = async () => {
    try {
      const updated = await commentsApi.updateComment(
        comment.articleId,
        comment.id,
        editContent,
      );
      onUpdated(updated);
      setEditing(false);
      notify(hr.comments.updated, "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleDelete = async () => {
    try {
      await commentsApi.deleteComment(comment.articleId, comment.id);
      onDeleted(comment.id);
      notify(hr.comments.deleted, "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  return (
    <Box sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {comment.user?.username || hr.comments.fallbackUser}
        </Typography>
        <TimeAgo date={comment.createdAt} />
        {(canEdit || canDelete) && !editing && (
          <Box sx={{ ml: "auto" }}>
            {canEdit && (
              <IconButton size="small" onClick={() => setEditing(true)}>
                <Edit fontSize="small" />
              </IconButton>
            )}
            {canDelete && (
              <IconButton size="small" onClick={handleDelete}>
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      {editing ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            size="small"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
          />
          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleUpdate}>
              {hr.common.save}
            </Button>
            <IconButton size="small" onClick={() => setEditing(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2">{comment.content}</Typography>
      )}
    </Box>
  );
}
